import React, { useEffect, useRef, useState } from "react";
import { logResponse } from "../utils/logger";
import { useNavigate } from "react-router-dom";
import { fetchGetCaptcha, fetchLogin, fetchNow } from "../api";
import authImageFallback from "../assets/img/img_auth.svg";
import SubmitButton from "../components/SubmitButton";

const Home = () => {
  const formRef = useRef();
  const resultRef = useRef();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [captchaImage, setCaptchaImage] = useState(null);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaText, setCaptchaText] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [storedEmail, setStoredEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const isSubmittingRef = useRef(false);

  const handleClick = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      const formData = new FormData(formRef.current);
      const data = Object.fromEntries(formData.entries());
      data["captcha_token"] = captchaToken;

      const res = await fetchLogin(data);
      logResponse("req:", data);
      logResponse("res:", res);
      resultRef.current.value = JSON.stringify(res, null, 2);

      if (res.access_token) {
        localStorage.setItem("login_triggered", "true");
        localStorage.setItem("access_token", res.access_token);
        if (res.user.email) localStorage.setItem("email", res.user.email);
        if (res.user.customer_number) localStorage.setItem("customer_number", res.user.customer_number);
        setErrorMessage("");
        window.dispatchEvent(new Event("storage"));
        navigate("/mypage");
      } else {
        let message = "ログインに失敗しました。";
        if (res.message?.includes("verify hmac False")) {
          message = "画像認証に失敗しました。再度入力をお願いします。";
          setCaptchaText("");
          const captcha = await fetchGetCaptcha();
          setCaptchaImage(captcha.image);
          setCaptchaToken(captcha.token);
        } else {
          message = "入力されたメールアドレスまたはパスワードに誤りがあります。";
        }
        setErrorMessage(message);
      }
    } catch (err) {
      const fallback = err?.message || "通信エラーが発生しました。";
      setErrorMessage(fallback);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const email = localStorage.getItem("email");
    if (email) setStoredEmail(email);
  }, []);

  useEffect(() => {
    fetchGetCaptcha().then((data) => {
      setCaptchaImage(data.image);
      setCaptchaToken(data.token);
      setLoading(false);
    });
  }, []);

  return (
    <>
      <section className="section form-section section-block">
        <form className="login-form" ref={formRef} noValidate>
          {/* メールアドレス */}
          <div className="form-group">
            <label htmlFor="email">メールアドレス</label>
            <div className="overlap-group-wrapper">
              <div className="overlap-group">
                <input
                  type="email"
                  name="email"
                  placeholder="exsample@gmail.com"
                  required
                  autoComplete="email"
                  aria-describedby="email-error"
                  defaultValue={storedEmail}
                />
              </div>
            </div>
          </div>

          {/* パスワード */}
          <div className="form-group">
            <label htmlFor="password">パスワード</label>
            <div className="group-2 password-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                required
                autoComplete="current-password"
                aria-describedby="password-error"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="toggle-password"
                aria-label={showPassword ? "パスワードを隠す" : "パスワードを表示する"}
              >
                <i className={`fa-solid ${showPassword ? "fa-eye" : "fa-eye-slash"}`}></i>
              </button>
            </div>
          </div>

          {/* 画像認証 */}
          <div className="form-group">
            <div className="auth">
              {loading ? (
                <p>画像読み込み中...</p>
              ) : (
                <img src={captchaImage || authImageFallback} alt="画像認証用のキャプチャ画像" />
              )}
            </div>
            <label htmlFor="captcha_text">【画像認証】上記の文字をご入力ください</label>
            <div className="group-3">
              <input
                type="text"
                name="captcha_text"
                value={captchaText}
                onChange={(e) => setCaptchaText(e.target.value)}
                required
                autoComplete="off"
                aria-describedby="captcha-error"
              />
            </div>
          </div>

          {/* ログインボタン */}
          <div className="btn-green">
            <SubmitButton
              type="button"
              onClick={handleClick}
              isSubmitting={isSubmitting}
              label="ログイン"
               wrap={false}
            />
          </div>

          {/* エラーメッセージ表示 */}
          {errorMessage && (
            <div className="error-message">
              <p style={{ color: "red", fontWeight: "bold" }}>{errorMessage}</p>
            </div>
          )}

          <p className="form-text">
            <a className="bold underline" href="/forget">
              パスワードをお忘れの方はこちら
            </a>
          </p>

          {/* 実行結果（任意） */}
          <textarea ref={resultRef} readOnly rows="6" style={{ display: "none" }} />
        </form>
      </section>

      {/* 新規登録セクション */}
      <section className="section registration-section">
        <p className="form-text tc">ご登録をされていませんか？（新規登録）</p>
        <div className="btn-orange-2">
          <a href="/signup?customer_type=new">
            <span>商品購入したことが「ない」方はこちら</span>
          </a>
        </div>
        <div className="btn-orange">
          <a href="/signup?customer_type=existing">
            <span>商品購入したことが「ある」方はこちら</span>
          </a>
        </div>
      </section>
    </>
  );
};

export default Home;
